import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import axios from "axios";
import { FETCH_CATEGORIES } from "../_actions/endpoints";
import { ShowCenterSpinner } from "../_components/common/statelessView";
import TLDRInfiniteScroll from "../_components/common/TLDRInfiniteScroll";
import { StyledAiTemplate } from "../_components/styled/ai/styledAi";
import { debounce } from "lodash";
import { StyledAICategories } from "../_components/styled/styles";
import Format from "string-format";
import { CATEGORY_COPY } from "../_components/details/constants";
import { analyticsTrackEvent, toPascalCase } from "../_utils/common";
import { fetchAiTemplates, resetDocument } from "../_actions/aiDocumentActions";
import Drawer from "../_components/drawer/Drawer";
import { handleHTTPError } from "../_actions/errorHandlerActions";
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Tooltip,
  Button,
  Heading,
  Flex,
  Box,
  Divider,
  Stack,
} from "@chakra-ui/react";
import {
  FaArrowCircleLeft,
  FaPencilAlt,
  FaSearch,
  FaTimesCircle,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import * as ReactIcon from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { AI_ASSISTANT_KEY, MAIN_CONTAINER_SCROLLABLE_TARGET_ID } from "../_utils/constants";
import TLDRTitleSearch from "../_components/common/TLDRTitleSearch";
import TldrMobileSearchSuggestion from '../_components/common/TldrMobileSearchSuggestion';

export const AITemplates = (props) => {
  const { loadMore, page, isMobileView } = props;
  const { t } = useTranslation(["ai", "common"]);
  const [loading, isLoaded] = useState(false);
  const [isCategoriesDrawerOpen, setIsCategoriesDrawrOpen] = useState(false);
  const [filter, setFilter] = useState(props.data?.tab || "all");
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [templates, setTemplates] = useState([]);
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false);

  let signalToken = axios.CancelToken.source();

  function getFilteredCategory(newResults = categories) {
    let categoryId = "";
    if (filter && filter !== "all") {
      let selectedCategory = newResults.find((a) => a?.id === filter);
      categoryId = selectedCategory?.id ?? "";
    }
    return categoryId;
  }

  function fetchCategories() {
    axios
      .get(Format(FETCH_CATEGORIES, CATEGORY_COPY), {
        cancelToken: signalToken.token,
      })
      .then((res) => {
        let newResults = res.data.results;
        setCategories(
          [{ id: "all", title: "all", display_name: "All" }].concat(newResults)
        );

        const categoryId = getFilteredCategory();
        fetchAiTemplates(categoryId);
      })
      .catch((error) => { });
  }

  function fetchAiTemplates(key = "", page, searchTerm) {
    isLoaded(false);
    props
      .fetchAiTemplates(props, key, signalToken, page, searchTerm)
      .then(() => {
        isLoaded(true);
      });
  }

  function loadMoreData() {
    const categoryId = getFilteredCategory();
    fetchAiTemplates(categoryId, page, searchText);
  }

  const onInputChange = (term) => {
    props.resetAiTemplates();
    if (term) {
      const categoryId = getFilteredCategory();
      fetchAiTemplates(categoryId, 1, term);
    } else {
      setTemplates([]);
      clearSearch();
    }
  };

  function clearSearch() {
    setSearchText("");
    setTemplates([]);
    props.resetAiTemplates();
    const categoryId = getFilteredCategory();
    fetchAiTemplates(categoryId, 1);
  }
  const debounceFn = useCallback(debounce(onInputChange, 500), []);

  useEffect(() => {
    fetchCategories(CATEGORY_COPY);
    return () => {
      signalToken.cancel("The user aborted a request.");
      props.resetAiTemplates();
    };
  }, []);

  useEffect(() => {
    if (searchText && page < 2) {
      setTemplates(props.aiTemplates);
    } else {
      setTemplates([...templates, ...props.aiTemplates]);
    }
  }, [props.aiTemplates]);

  function onFilterChange(key) {
    setSearchText("");
    if (key === "all") props.resetAiTemplates();
    setTemplates([]);
    setFilter(key);
    fetchAiTemplates(key === "all" ? "" : key);
  }

  function handleChange(searchText) {
    setSearchText(searchText);
  }

  function getSelectedCategory() {
    const selectedCategory = categories.find((a) => a?.id === filter);
    return selectedCategory;
  }

  function handleSearchFocusOnMobile() {
    setShowMobileSuggestions(true);
  };

  function handleSearchBlurOnMobile() {
    setShowMobileSuggestions(false);
  };

  const childElements = templates.map((result) => {
    return (
      <StyledAiTemplate
        key={`${result.id}`}
        className={
          result.id === props.wizardData?.goals?.template.id
            ? "ai-selected"
            : ""
        }
        onClick={(e) => {
          props.onComplete("goals", result);
          analyticsTrackEvent("selectedAIGoal", {
            template: result.title,
          });
        }}
      >
        <div className="icon new-document-icon" key={`${result.id}_icon`}>
          {result?.content?.icon ? (
            ReactIcon[`Fa${toPascalCase(result.content.icon)}`] ? (
              React.createElement(
                ReactIcon[`Fa${toPascalCase(result.content.icon)}`]
              )
            ) : (
              <FaPencilAlt size={28} />
            )
          ) : (
            <FaPencilAlt size={28} />
          )}
        </div>
        <div className="info-container">
          <div className="title" key={`${result.id}_title`}>
            {result.title}
          </div>
          {result.description.length >= 70 ? (
            <Tooltip hasArrow placement="bottom" label={result.description}>
              <div className="subtitle description">{result.description}</div>
            </Tooltip>
          ) : (
            <div className="subtitle" key={`${result.id}_sub`}>
              {result.description}
            </div>
          )}
        </div>
      </StyledAiTemplate>
    );
  });

  const categoriesView = categories.map((category, index) => {
    return (
      <Button
        key={index}
        variant="outline"
        borderRadius="full"
        color="gray.100"
        borderColor={filter === category?.id ? "primary.400" : "transparent"}
        _hover={{ bg: "none", borderColor: "primary.400" }}
        _focus={{ borderColor: "primary.400", bg: "none" }}
        _active={{ borderColor: "primary.400", bg: "none" }}
        mr="1rem"
        onClick={() => onFilterChange(category.id)}
      >
        {category.display_name}
      </Button>
    );
  });

  return (
    <>
      <ShowCenterSpinner loaded={loading}>
        {t("templates.spinner")}
      </ShowCenterSpinner>

      <Box className="ai-templates-header-row">
        {!isMobileView && (
          <Heading
            color="gray.100"
            fontSize={["20px", "24px"]}
            fontWeight="normal"
            py="0.5"
            w={["100%", "70%"]}
            display="flex"
            alignItems="center"
          >
            <FaArrowCircleLeft
              className="back-btn"
              onClick={() => {
                props.onComplete();
              }}
            />
            {t("templates.heading")}
          </Heading>
        )}

        {!isMobileView ? (
          <Flex m="0" alignItems="center" h="fit-content" w="40%">
            <InputGroup color="white">
              <InputLeftElement children={<FaSearch />} />
              <Input
                placeholder={t("templates.search.other")}
                aria-label="Search"
                onChange={(e) => handleChange(e.target.value)}
                onKeyUp={(e) => {
                  if (e.keyCode === 13) {
                    debounceFn(searchText);
                  }
                }}
                value={searchText}
                autoFocus={true}
              />
              {searchText !== "" && (
                <InputRightElement
                  children={<FaTimesCircle onClick={clearSearch} />}
                />
              )}
            </InputGroup>
          </Flex>
        ) : null}
      </Box>

      {isMobileView && (
        <>
          <TLDRTitleSearch
            searchConfig={{
              onClear: clearSearch,
              onFocus: handleSearchFocusOnMobile,
            }}
            searchText={searchText}
            placeholder={t("templates.search.other")}
            extraSx={{
              paddingX: "0"
            }}
          />

          {showMobileSuggestions ? (
            <TldrMobileSearchSuggestion
              searchTerm={searchText}
              searchInFormats={false}
              isOpen={showMobileSuggestions}
              searchConfig={{
                handleSearch: handleChange,
                onBlur: handleSearchBlurOnMobile,
                onClear: clearSearch,
                handleEnterKeyEvent: () => {
                  setShowMobileSuggestions(false);
                  debounceFn(searchText);
                },
              }}
              placeholder={t("search_docs")}
            />
          ) : null}
        </>
      )}

      {!isMobileView ? (
        <Box className={`no-scrollbar mob-my-3`} width="100%">
          <StyledAICategories>{categoriesView}</StyledAICategories>
        </Box>
      ) : (
        <>
          <Button
            rightIcon={
              isCategoriesDrawerOpen ? (
                <FaChevronUp />
              ) : (
                <FaChevronDown />
              )
            }
            onClick={() =>
              setIsCategoriesDrawrOpen(true)
            }
            isLoading={!categories.length}
          >
            {categories.length && filter !== "all" ? getSelectedCategory()?.display_name : "Categories"}
          </Button>

          <Drawer
            isOpen={isCategoriesDrawerOpen}
            onClose={() => setIsCategoriesDrawrOpen(false)}
            placement="bottom"
          >
            <Flex alignItems="center">
              <Heading as="h5" size="md" mr="2">
                {t("filters.heading", { ns: "common" })}
              </Heading>
            </Flex>
            <Divider mt="2" mb="4" ml="-6" mr="-6" width="auto" />
            <Stack
              spacing="4"
              mb="4"
              maxH="430px"
              overflowY="auto"
              sx={{
                "-ms-overflow-style": "none",
                "scrollbar-width": "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              {categories &&
                categories.map((category) => (
                  <Heading
                    as="h6"
                    size="sm"
                    m="0"
                    key={category.id}
                    onClick={() => {
                      setIsCategoriesDrawrOpen(false);
                      onFilterChange(category.id);
                    }}
                    color={filter === category?.id && "primary.200"}
                  >
                    {category.display_name}
                  </Heading>
                ))}
            </Stack>
          </Drawer>
        </>
      )}
      <hr className="tldr-hl"></hr>
      <TLDRInfiniteScroll
        className={`ai-new-document-infinite-scroll web-p-3`}
        childrens={childElements}
        hasMore={loadMore}
        loadMoreData={loadMoreData}
        scrollableTarget={MAIN_CONTAINER_SCROLLABLE_TARGET_ID}
        loaded={false} // Change its value whenever data get loaded.
      />
    </>
  );
};

AITemplates.propTypes = {
  auth: PropTypes.object.isRequired,
  aiTemplates: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  aiTemplates: state.document.aiTemplates,
  page: state.document.page,
  loadMore: state.document.loadMore,
  subscription: state.subscription,
  isMobileView: state.app.isMobileView,
});

const mapDispatchToProps = (dispatch) => ({
  fetchAiTemplates: (props, key, signalToken, page, searchTerm) =>
    dispatch(fetchAiTemplates(props, key, signalToken, page, searchTerm)),
  resetAiTemplates: () => dispatch(resetDocument()),
  handleHTTPError: (error, props) => dispatch(handleHTTPError(error, props)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AITemplates);
